/** @format */

import type { FC } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../core/button/Button";
import PrivacySettings from "../../organisms/privacySettings/PrivacySettings";
import { useModals } from "../modalStack/ModalProvider";
import type { PreferenceBannerProps } from "./PreferenceBanner.types";

const PreferenceBanner: FC<PreferenceBannerProps> = ({
  handleSetPreferences,
}) => {
  const { t } = useTranslation();
  const { openModal, closeModal } = useModals();

  // Insert zero-width spaces to obfuscate common blocker keywords without changing visible text
  const ZERO_WIDTH_SPACE = "\u200B";
  const obfuscate = (value: string): string =>
    value.replace(/\S/g, (char) => char + ZERO_WIDTH_SPACE);

  const handleOpenPrivacySettings = () => {
    openModal(<PrivacySettings handleSetPreferences={handleSetPreferences} />, {
      zIndex: 1001,
      id: "privacySettings",
      size: "lg",
      ariaTitle: t("cookies:cookiesConfigurator.title"),
    });
  };

  return (
    <div className="w-full h-full flex flex-col items-center justify-center py-8 px-5 gap-2">
      <h2 className="text-center heading-2xl">
        🍪 {obfuscate(t("cookies:banner.titleShort"))}
      </h2>

      <div className="flex flex-col gap-2.5">
        <p className="text-center body-sm text-text-default">
          {obfuscate(t("cookies:banner.text"))}
          <button
            type="button"
            className="text-blue-600 underline hover:text-blue-800 cursor-pointer pointer-events-auto pl-1.5"
            onClick={() => {
              handleOpenPrivacySettings();
            }}
            tabIndex={0}
            aria-label={t("cookies:banner.openCookiesConfigurator")}
          >
            {obfuscate(t("cookies:banner.openCookiesConfigurator"))}
          </button>
        </p>
      </div>

      <div className="flex flex-col items-center gap-4 mt-3">
        <Button
          text={obfuscate(t("cookies:banner.acceptOnlyNecessary"))}
          className="text-[17px] whitespace-nowrap"
          variant="basic"
          size="xl"
          onClick={() => {
            handleSetPreferences("minimum");
            closeModal();
          }}
        />
        <Button
          variant="primary"
          size="xl"
          text={obfuscate(t("cookies:banner.accept"))}
          onClick={() => {
            handleSetPreferences("all");
            closeModal();
          }}
        />
      </div>
    </div>
  );
};

export default PreferenceBanner;
